import React, { useContext, useState, useEffect, useRef, useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Gear, BoxArrowRight, List, SunFill, MoonStarsFill, ChevronDown, Book, Envelope } from 'react-bootstrap-icons';
import { AuthContext } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import JoinBetaButton from './JoinBetaButton';
import HomeAccentColorPicker from './HomeAccentColorPicker';
import useLandingStylingParams from '../../hooks/useLandingStylingParams';
import { getAppNavRoutes } from '../../utils/appNavRoutes';
import { FEATURES_PAGE_SECTION_LINKS } from '../../data/featuresPageNav';

/** Home-page-only light/dark toggle for visually testing the marketing site on a white background. */
function HomeThemeToggleButton() {
  const { isDark, toggleTheme, themeName, switchTheme } = useTheme();
  const { stylingAllowed, setThemeParam } = useLandingStylingParams();

  const handleToggle = () => {
    const nextTheme = themeName === 'dark' ? 'light' : 'dark';
    switchTheme(nextTheme);
    if (stylingAllowed) setThemeParam(nextTheme);
  };

  return (
    <button
      type="button"
      onClick={handleToggle}
      aria-label={isDark ? 'Switch to light theme' : 'Switch to dark theme'}
      title={isDark ? 'Switch to light theme' : 'Switch to dark theme'}
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: '38px',
        height: '38px',
        borderRadius: '50%',
        border: isDark ? '1px solid rgba(255,255,255,0.16)' : '1px solid rgba(15,23,42,0.14)',
        background: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(15,23,42,0.04)',
        color: isDark ? '#fff' : '#0f172a',
        cursor: 'pointer',
        flexShrink: 0,
        transition: 'background-color 0.2s',
      }}
      onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = isDark ? 'rgba(255,255,255,0.14)' : 'rgba(15,23,42,0.08)'; }}
      onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = isDark ? 'rgba(255,255,255,0.06)' : 'rgba(15,23,42,0.04)'; }}
    >
      {isDark ? <SunFill size={16} aria-hidden /> : <MoonStarsFill size={16} aria-hidden />}
    </button>
  );
}

/** Closes a flyout panel when a click or touch lands outside its ref element. */
function useOutsideClick(ref, isOpen, setter) {
  const stableSetter = useCallback(setter, []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!isOpen) return;
    const handle = (e) => {
      const target = e.target;
      if (!ref.current) return;
      if (!(target instanceof Node) || !ref.current.contains(target)) stableSetter(false);
    };
    document.addEventListener('mousedown', handle);
    document.addEventListener('touchstart', handle);
    return () => {
      document.removeEventListener('mousedown', handle);
      document.removeEventListener('touchstart', handle);
    };
  }, [isOpen, ref, stableSetter]);
}

/** True when pathname matches the nav link destination (tier-aware app + marketing routes). */
function isCurrentNavDestination(pathname, to) {
  const target = to.replace(/\/$/, '') || '/';
  if (target === '/dashboard') {
    return pathname === '/dashboard'
      || (pathname.startsWith('/dashboard/') && !pathname.startsWith('/dashboard/trade'));
  }
  if (target === '/home' || target === '/landing' || target === '/features' || target === '/pricing' || target === '/brokers' || target === '/learn' || target === '/blog' || target === '/contact' || target === '/quiz') {
    return pathname === target;
  }
  return pathname === target || pathname.startsWith(`${target}/`);
}

/**
 * Shared navigation component for landing-style pages (Landing, ContactUs, Privacy, Terms, Legal, etc.)
 * Features the user dropdown with avatar from the Landing page design.
 */
const LandingNav = () => {
  const { user, logout, isInitializing } = useContext(AuthContext) || {};
  const location = useLocation();
  const [accountMenuOpen, setAccountMenuOpen] = useState(false);
  const [guestMenuOpen, setGuestMenuOpen] = useState(false);
  const [featuresMenuOpen, setFeaturesMenuOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const accountMenuRef = useRef(null);
  const guestMenuRef = useRef(null);
  const featuresMenuRef = useRef(null);
  /** Marketing home (`/landing`; legacy `/home` redirects here): CTA first; Pricing in flyout on desktop. */
  const isMarketingHome = location.pathname === '/landing' || location.pathname === '/home';
  const isHomePage = isMarketingHome;
  const isAuthPage = location.pathname === '/login' || location.pathname === '/register';
  const { stylingAllowed } = useLandingStylingParams();
  const showStylingTools = isHomePage && stylingAllowed;
  const pathname = location.pathname;

  // Elevated z-index on JSON landing (/landing?…) and legacy /lp/v* routes
  const isJsonLanding = location.pathname === '/landing';
  const isLandingV2 = location.pathname === '/lp/v2' || location.search.includes('lp=v2');
  const isLandingV3 = location.pathname === '/lp/v3' || location.search.includes('lp=v3');
  const useElevatedLandingNavZIndex = isJsonLanding || isLandingV2 || isLandingV3;
  const dropdownZIndex = useElevatedLandingNavZIndex ? 1002 : 1000;
  const backdropZIndex = useElevatedLandingNavZIndex ? 1001 : 999;

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useOutsideClick(guestMenuRef, guestMenuOpen, setGuestMenuOpen);
  useOutsideClick(accountMenuRef, accountMenuOpen, setAccountMenuOpen);
  useOutsideClick(featuresMenuRef, featuresMenuOpen, setFeaturesMenuOpen);

  useEffect(() => {
    setAccountMenuOpen(false);
    setGuestMenuOpen(false);
    setFeaturesMenuOpen(false);
  }, [pathname]);

  // var(--landing-nav-text) is only defined on the landing page's light theme (see Landing.css);
  // everywhere else (dashboard, onboarding, etc.) the #fff fallback keeps the existing dark-shell look.
  const navLinkStyle = {
    color: 'var(--landing-nav-text, #fff)',
    textDecoration: 'none',
    fontSize: 'clamp(0.8em, 1.7vw, 0.95em)',
    opacity: 0.9,
    transition: 'opacity 0.2s',
    whiteSpace: 'nowrap'
  };

  // Flyout dropdown panel keeps its own always-dark chrome (see flyoutPanelStyle) regardless of
  // page theme, so this text stays plain white rather than following --landing-nav-text.
  const flyoutLinkStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    padding: '10px 16px',
    color: '#fff',
    textDecoration: 'none',
    fontSize: '0.95em',
    transition: 'background 0.2s'
  };

  const flyoutBlockStyle = {
    display: 'block',
    padding: '10px 14px',
    borderRadius: '10px',
    color: '#fff',
    textDecoration: 'none',
    fontSize: '0.95em',
    transition: 'background 0.15s',
  };

  const handleLinkHover = (e, entering) => {
    e.target.style.opacity = entering ? '1' : '0.9';
  };

  const flyoutHoverHandlers = {
    onMouseEnter: (e) => { e.currentTarget.style.background = 'rgba(255,255,255,0.08)'; },
    onMouseLeave: (e) => { e.currentTarget.style.background = 'transparent'; },
  };

  const appNav = user ? getAppNavRoutes(user) : null;

  const closeAccountMenu = () => setAccountMenuOpen(false);
  const closeGuestMenu = () => setGuestMenuOpen(false);
  const closeFeaturesMenu = () => setFeaturesMenuOpen(false);

  const featuresDropdownLinks = [
    { key: 'features-overview', to: '/features', label: 'All features' },
    ...FEATURES_PAGE_SECTION_LINKS.map(({ hash, label }) => ({
      key: hash,
      to: `/features${hash}`,
      label,
    })),
  ];

  const renderFeaturesDropdownLinks = (onNavigate, linkStyle) =>
    featuresDropdownLinks.map(({ key, to, label }) => {
      const onFeatures = location.pathname === '/features';
      const current = onFeatures && (
        key === 'features-overview' ? location.hash === '' : location.hash === key
      );
      return (
        <Link
          key={key}
          to={to}
          role="menuitem"
          aria-current={current ? 'true' : undefined}
          onClick={onNavigate}
          style={linkStyle}
          {...flyoutHoverHandlers}
        >
          {label}
        </Link>
      );
    });

  const renderFeaturesNavDropdown = () => (
    <div
      ref={featuresMenuRef}
      style={{
        position: 'relative',
        flexShrink: 0,
        zIndex: featuresMenuOpen ? dropdownZIndex : undefined,
      }}
    >
      <button
        type="button"
        aria-expanded={featuresMenuOpen}
        aria-haspopup="menu"
        aria-label="Features menu"
        onClick={() => setFeaturesMenuOpen((o) => !o)}
        style={{
          ...navLinkStyle,
          display: 'inline-flex',
          alignItems: 'center',
          gap: '4px',
          padding: 0,
          background: 'transparent',
          border: 'none',
          cursor: 'pointer',
          fontFamily: 'inherit',
        }}
        onMouseEnter={(e) => { e.currentTarget.style.opacity = '1'; }}
        onMouseLeave={(e) => { e.currentTarget.style.opacity = '0.9'; }}
      >
        Features
        <ChevronDown
          size={12}
          aria-hidden
          style={{
            opacity: 0.85,
            transform: featuresMenuOpen ? 'rotate(180deg)' : 'none',
            transition: 'transform 0.15s ease',
          }}
        />
      </button>
      {featuresMenuOpen && (
        <>
          {flyoutBackdrop()}
          <div
            role="menu"
            className="landing-nav-features-flyout"
            style={{
              ...flyoutPanelStyle,
              right: 'auto',
              left: 0,
              minWidth: '220px',
            }}
          >
            {renderFeaturesDropdownLinks(closeFeaturesMenu, flyoutLinkStyle)}
          </div>
        </>
      )}
    </div>
  );

  /** Invisible scrim; pointer-events off so flyout Links receive clicks (fixed layer otherwise wins over absolute panel). */
  const flyoutBackdrop = () => (
    <div
      aria-hidden
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: backdropZIndex,
        pointerEvents: 'none',
      }}
    />
  );

  const flyoutPanelStyle = {
    position: 'absolute',
    top: 'calc(100% + 10px)',
    right: 0,
    background: '#0C1018',
    border: '1px solid #2A3444',
    borderRadius: '10px',
    padding: '6px',
    minWidth: '220px',
    boxShadow: '0 12px 32px rgba(7, 9, 14, 0.7)',
    zIndex: dropdownZIndex,
  };

  const renderFlyoutSectionLabel = (label) => (
    <div style={{
      padding: '6px 14px 4px',
      fontSize: '0.7em',
      fontWeight: 600,
      letterSpacing: '0.06em',
      textTransform: 'uppercase',
      color: '#a0aec0',
    }}>
      {label}
    </div>
  );

  const guestPrimaryLinks = [
    ...(!isCurrentNavDestination(pathname, '/landing') && !isCurrentNavDestination(pathname, '/home')
      ? [{ label: 'Home', to: '/landing' }]
      : []),
    // Auth pages keep a slim header — Brokers/Blog/Pricing live in the menu (or elsewhere).
    ...(!isAuthPage && !isCurrentNavDestination(pathname, '/brokers')
      ? [{ label: 'Brokers', to: '/brokers' }]
      : []),
    ...(!isAuthPage && !isCurrentNavDestination(pathname, '/blog')
      ? [{ label: 'Blog', to: '/blog' }]
      : []),
    ...(!isAuthPage && !isHomePage && !isCurrentNavDestination(pathname, '/pricing')
      ? [{ label: 'Pricing', to: '/pricing' }]
      : []),
  ];

  /**
   * Logged-in marketing header: Trade is the leftmost primary pill.
   * Dashboard stays a text link when Trade is not available.
   */
  const loggedInPrimaryLinks = [];
  let tradeNavLink = null;
  if (user && appNav) {
    if (appNav.showTrade && !isCurrentNavDestination(pathname, appNav.tradePath)) {
      tradeNavLink = { label: 'Trade', to: appNav.tradePath };
      loggedInPrimaryLinks.push(tradeNavLink);
    } else if (appNav.showDashboard && !isCurrentNavDestination(pathname, appNav.dashboardPath)) {
      loggedInPrimaryLinks.push({ label: 'Dashboard', to: appNav.dashboardPath });
    }
  }
  const loggedInPrimaryPaths = new Set(
    !isMobile
      ? [
          ...guestPrimaryLinks.map((l) => l.to),
          '/features',
          ...loggedInPrimaryLinks.map((l) => l.to),
        ]
      : []
  );

  /** Paths shown as top-level guest links or a single mobile Features link (desktop Features is a dropdown). */
  const guestPathsInPrimaryOrMobileHeader = new Set([
    ...guestPrimaryLinks.map((l) => l.to),
    '/features',
  ]);

  /** Slim flyout for /login and /register — Pricing + Log in / Register only. */
  const renderAuthFlyoutLinks = (onNavigate, linkStyle) => {
    const links = [];
    if (!isCurrentNavDestination(pathname, '/pricing')) {
      links.push({ key: 'pricing', to: '/pricing', label: 'Pricing' });
    }
    if (!isCurrentNavDestination(pathname, '/login')) {
      links.push({ key: 'login', to: '/login', label: 'Log in' });
    }
    if (!isCurrentNavDestination(pathname, '/register')) {
      links.push({ key: 'register', to: '/register', label: 'Register' });
    }
    return links.map(({ key, to, label }) => (
      <Link
        key={key}
        to={to}
        role="menuitem"
        onClick={onNavigate}
        style={linkStyle}
        {...flyoutHoverHandlers}
      >
        {label}
      </Link>
    ));
  };

  /**
   * Logged-in overflow menu. Desktop already shows the same page links as guests,
   * so those stay out of this list. Learn and Contact us live on the account menu.
   */
  const renderLoggedInNavigateFlyoutLinks = (onNavigate, linkStyle) => {
    const links = [];
    const inBar = (to) => loggedInPrimaryPaths.has(to);
    if (!isCurrentNavDestination(pathname, '/landing') && !isCurrentNavDestination(pathname, '/home') && !inBar('/landing')) {
      links.push({ key: 'home', to: '/landing', label: 'Home' });
    }
    if (appNav) {
      if (appNav.showTrade) {
        if (!isCurrentNavDestination(pathname, appNav.tradePath) && !loggedInPrimaryPaths.has(appNav.tradePath)) {
          links.push({ key: 'trade', to: appNav.tradePath, label: 'Trade' });
        }
      } else if (
        appNav.showDashboard
        && !isCurrentNavDestination(pathname, appNav.dashboardPath)
        && !loggedInPrimaryPaths.has(appNav.dashboardPath)
      ) {
        links.push({ key: 'dashboard', to: appNav.dashboardPath, label: 'Dashboard' });
      }
    }
    if (!isCurrentNavDestination(pathname, '/brokers') && !inBar('/brokers')) {
      links.push({ key: 'brokers', to: '/brokers', label: 'Brokers' });
    }
    if (!isCurrentNavDestination(pathname, '/blog') && !inBar('/blog')) {
      links.push({ key: 'blog', to: '/blog', label: 'Blog' });
    }
    if (!isCurrentNavDestination(pathname, '/features') && !inBar('/features')) {
      links.push({ key: 'features', to: '/features', label: 'Features' });
    }
    if (!isCurrentNavDestination(pathname, '/pricing') && !inBar('/pricing')) {
      links.push({ key: 'pricing', to: '/pricing', label: 'Pricing' });
    }
    return links.map(({ key, to, label }) => (
      <Link
        key={key}
        to={to}
        role="menuitem"
        onClick={onNavigate}
        style={linkStyle}
        {...flyoutHoverHandlers}
      >
        {label}
      </Link>
    ));
  };

  const renderNavigateFlyoutLinks = (onNavigate, linkStyle, { forGuest = false } = {}) => {
    if (forGuest && isAuthPage) {
      return renderAuthFlyoutLinks(onNavigate, linkStyle);
    }

    const links = [];
    const skipGuestDuplicate = (to) => forGuest && guestPathsInPrimaryOrMobileHeader.has(to);

    // Desktop guest Menu needs Home; mobile already lists it via guestPrimaryLinks.
    if (
      !isCurrentNavDestination(pathname, '/landing')
      && !isCurrentNavDestination(pathname, '/home')
      && !(forGuest && isMobile)
    ) {
      links.push({ key: 'home', to: '/landing', label: 'Home' });
    }

    if (!isCurrentNavDestination(pathname, '/brokers') && !skipGuestDuplicate('/brokers')) {
      links.push({ key: 'brokers', to: '/brokers', label: 'Brokers' });
    }
    const pricingInGuestPrimary = forGuest && !isMobile && !isHomePage;
    if (!isCurrentNavDestination(pathname, '/pricing') && (isHomePage || !pricingInGuestPrimary) && !skipGuestDuplicate('/pricing')) {
      links.push({ key: 'pricing', to: '/pricing', label: 'Pricing' });
    }
    if (!isCurrentNavDestination(pathname, '/learn')) {
      links.push({ key: 'learn', to: '/learn', label: 'Learn' });
    }
    if (!isCurrentNavDestination(pathname, '/blog') && !skipGuestDuplicate('/blog')) {
      links.push({ key: 'blog', to: '/blog', label: 'Blog' });
    }
    if (!isCurrentNavDestination(pathname, '/contact')) {
      links.push({ key: 'contact', to: '/contact', label: 'Contact us' });
    }
    if (!user && !isCurrentNavDestination(pathname, '/register')) {
      links.push({ key: 'register', to: '/register', label: 'Register' });
    }
    if (!isCurrentNavDestination(pathname, '/quiz')) {
      links.push({ key: 'quiz', to: '/quiz', label: 'Take the FREE Quiz' });
    }

    return links.map(({ key, to, label }) => (
      <Link
        key={key}
        to={to}
        role="menuitem"
        onClick={onNavigate}
        style={linkStyle}
        {...flyoutHoverHandlers}
      >
        {label}
      </Link>
    ));
  };

  const renderMenuTriggerButton = (open, onToggle, ariaLabel) => (
    <button
      type="button"
      aria-expanded={open}
      aria-haspopup="menu"
      aria-label={ariaLabel}
      onClick={onToggle}
      style={{
        padding: '6px',
        backgroundColor: 'transparent',
        border: 'none',
        borderRadius: '8px',
        color: 'var(--landing-nav-text, #fff)',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        transition: 'background-color 0.2s',
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.1)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.backgroundColor = 'transparent';
      }}
    >
      <List size={22} aria-hidden />
    </button>
  );

  return (
    <nav
      className={`landing-nav${isAuthPage ? ' landing-nav--auth' : ''}`}
      style={{ width: '100%', borderBottom: '1px solid rgba(255,255,255,0.1)' }}
    >
      <div className="landing-nav-inner">
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          width: '100%'
        }}>
          <div className="nav-brand" style={{ display: 'flex', alignItems: 'center' }}>
            <Link to="/landing" style={{ display: 'flex', alignItems: 'center', gap: '8px', textDecoration: 'none', color: 'inherit' }}>
              <img
                src={`${process.env.PUBLIC_URL}/favicon.png`}
                alt="PrecisionTrader"
                style={{
                  width: '32px',
                  height: '32px',
                  borderRadius: '4px'
                }}
              />
              <h3 className="landing-nav-brand">PrecisionTrader</h3>
            </Link>
          </div>
          <div className="nav-links" style={{
            display: 'flex',
            alignItems: 'center',
            gap: 'clamp(12px, 3vw, 32px)',
            flexShrink: 0
          }}>
            {showStylingTools && (
              <div className="landing-home-header-tools" style={{ display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0 }}>
                <HomeAccentColorPicker />
                <HomeThemeToggleButton />
              </div>
            )}
            {user ? (
              <>
                {tradeNavLink && (
                  <Link
                    to={tradeNavLink.to}
                    className="landing-cta-btn landing-cta-btn--nav landing-nav-trade-btn"
                  >
                    {tradeNavLink.label}
                  </Link>
                )}
                {!isMobile && (
                  <>
                    {guestPrimaryLinks
                      .filter(({ to }) => to === '/landing')
                      .map(({ label, to }) => (
                        <Link
                          key={to}
                          to={to}
                          style={navLinkStyle}
                          onMouseEnter={(e) => handleLinkHover(e, true)}
                          onMouseLeave={(e) => handleLinkHover(e, false)}
                        >
                          {label}
                        </Link>
                      ))}
                    {renderFeaturesNavDropdown()}
                    {guestPrimaryLinks
                      .filter(({ to }) => to !== '/landing')
                      .map(({ label, to }) => (
                        <Link
                          key={to}
                          to={to}
                          style={navLinkStyle}
                          onMouseEnter={(e) => handleLinkHover(e, true)}
                          onMouseLeave={(e) => handleLinkHover(e, false)}
                        >
                          {label}
                        </Link>
                      ))}
                    {loggedInPrimaryLinks
                      .filter(({ label }) => label !== 'Trade')
                      .map(({ label, to }) => (
                      <Link
                        key={to}
                        to={to}
                        style={navLinkStyle}
                        onMouseEnter={(e) => handleLinkHover(e, true)}
                        onMouseLeave={(e) => handleLinkHover(e, false)}
                      >
                        {label}
                      </Link>
                    ))}
                  </>
                )}
                {!isInitializing && <JoinBetaButton variant="landing" />}
                <div
                  ref={accountMenuRef}
                  style={{
                    position: 'relative',
                    flexShrink: 0,
                    zIndex: accountMenuOpen ? dropdownZIndex : undefined,
                  }}
                >
                  <div
                    role="button"
                    tabIndex={0}
                    aria-expanded={accountMenuOpen}
                    aria-haspopup="menu"
                    aria-label="Account menu"
                    title={user.email || 'User Account'}
                    onClick={() => {
                      setGuestMenuOpen(false);
                      setAccountMenuOpen((o) => !o);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        setGuestMenuOpen(false);
                        setAccountMenuOpen((o) => !o);
                      }
                    }}
                    style={{
                      width: '32px',
                      height: '32px',
                      borderRadius: '50%',
                      background: '#5B6CFF',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: '#E8EDF4',
                      fontSize: '0.95em',
                      fontWeight: '600',
                      border: '1px solid #2A3444',
                      cursor: 'pointer'
                    }}
                  >
                    {user.email ? user.email.charAt(0).toUpperCase() : '?'}
                  </div>
                  {accountMenuOpen && (
                    <>
                      {flyoutBackdrop()}
                      <div
                        role="menu"
                        className="landing-nav-account-flyout"
                        style={{ ...flyoutPanelStyle, top: 'calc(100% + 10px)' }}
                      >
                        <div style={{ padding: '8px 14px 6px', borderBottom: '1px solid rgba(255,255,255,0.1)', marginBottom: '4px' }}>
                          <div style={{ fontSize: '0.85em', color: '#a0aec0' }}>Signed in as</div>
                          <div style={{ fontSize: '0.95em', color: '#fff', fontWeight: '500' }}>{user.email}</div>
                        </div>
                        <Link
                          to={appNav.settingsPath}
                          role="menuitem"
                          onClick={closeAccountMenu}
                          style={flyoutLinkStyle}
                          {...flyoutHoverHandlers}
                        >
                          <Gear size={16} aria-hidden /> Settings
                        </Link>
                        <Link
                          to="/learn"
                          role="menuitem"
                          onClick={closeAccountMenu}
                          style={flyoutLinkStyle}
                          {...flyoutHoverHandlers}
                        >
                          <Book size={16} aria-hidden /> Learn
                        </Link>
                        <Link
                          to="/contact"
                          role="menuitem"
                          onClick={closeAccountMenu}
                          style={flyoutLinkStyle}
                          {...flyoutHoverHandlers}
                        >
                          <Envelope size={16} aria-hidden /> Contact us
                        </Link>
                        <button
                          type="button"
                          role="menuitem"
                          onClick={() => { closeAccountMenu(); logout(); }}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '10px',
                            width: '100%',
                            padding: '10px 16px',
                            background: 'transparent',
                            border: 'none',
                            color: '#f87171',
                            fontSize: '0.95em',
                            cursor: 'pointer',
                            textAlign: 'left',
                            transition: 'background 0.2s'
                          }}
                          onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(255,255,255,0.08)'; }}
                          onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                        >
                          <BoxArrowRight size={16} aria-hidden /> Logout
                        </button>
                      </div>
                    </>
                  )}
                </div>
                {isMobile && (
                <div
                  ref={guestMenuRef}
                  style={{
                    position: 'relative',
                    flexShrink: 0,
                    zIndex: guestMenuOpen ? dropdownZIndex : undefined,
                  }}
                >
                  {renderMenuTriggerButton(
                    guestMenuOpen,
                    () => {
                      setAccountMenuOpen(false);
                      setGuestMenuOpen((o) => !o);
                    },
                    'Menu'
                  )}
                  {guestMenuOpen && (
                    <>
                      {flyoutBackdrop()}
                      <div
                        role="menu"
                        className="landing-nav-user-flyout"
                        style={{
                          ...flyoutPanelStyle,
                          ...(isMobile ? { maxHeight: 'min(70dvh, 32rem)', overflowY: 'auto' } : {}),
                        }}
                      >
                        {renderLoggedInNavigateFlyoutLinks(closeGuestMenu, flyoutBlockStyle)}
                      </div>
                    </>
                  )}
                </div>
                )}
              </>
            ) : (
              <>
                {!isMobile && (
                  <>
                    {guestPrimaryLinks
                      .filter(({ to }) => to === '/landing')
                      .map(({ label, to }) => (
                        <Link
                          key={to}
                          to={to}
                          style={navLinkStyle}
                          onMouseEnter={(e) => handleLinkHover(e, true)}
                          onMouseLeave={(e) => handleLinkHover(e, false)}
                        >
                          {label}
                        </Link>
                      ))}
                    {renderFeaturesNavDropdown()}
                    {guestPrimaryLinks
                      .filter(({ to }) => to !== '/landing')
                      .map(({ label, to }) => (
                        <Link
                          key={to}
                          to={to}
                          style={navLinkStyle}
                          onMouseEnter={(e) => handleLinkHover(e, true)}
                          onMouseLeave={(e) => handleLinkHover(e, false)}
                        >
                          {label}
                        </Link>
                      ))}
                  </>
                )}
                {!isMobile && !isAuthPage && (
                  <Link
                    to="/login"
                    style={navLinkStyle}
                    onMouseEnter={(e) => handleLinkHover(e, true)}
                    onMouseLeave={(e) => handleLinkHover(e, false)}
                  >
                    Log in
                  </Link>
                )}
                {!isInitializing && <JoinBetaButton variant="landing" />}
                {(isMobile || isAuthPage) && (
                <div
                  ref={guestMenuRef}
                  style={{
                    position: 'relative',
                    flexShrink: 0,
                    zIndex: guestMenuOpen ? dropdownZIndex : undefined,
                  }}
                >
                  {renderMenuTriggerButton(
                    guestMenuOpen,
                    () => setGuestMenuOpen((o) => !o),
                    'Menu'
                  )}
                  {guestMenuOpen && (
                    <>
                      {flyoutBackdrop()}
                      <div
                        role="menu"
                        className="landing-nav-guest-flyout"
                        style={{
                          ...flyoutPanelStyle,
                          ...(isMobile ? { maxHeight: 'min(70dvh, 32rem)', overflowY: 'auto' } : {}),
                        }}
                      >
                        {isMobile && !isAuthPage && guestPrimaryLinks.map(({ label, to }) => (
                          <Link
                            key={to}
                            to={to}
                            role="menuitem"
                            onClick={closeGuestMenu}
                            style={flyoutLinkStyle}
                            {...flyoutHoverHandlers}
                          >
                            {label}
                          </Link>
                        ))}
                        {isMobile && !isAuthPage && !isCurrentNavDestination(pathname, '/features') && (
                          <Link
                            to="/features"
                            role="menuitem"
                            onClick={closeGuestMenu}
                            style={flyoutLinkStyle}
                            {...flyoutHoverHandlers}
                          >
                            Features
                          </Link>
                        )}
                        {isMobile && isAuthPage && (
                          <Link
                            to="/landing"
                            role="menuitem"
                            onClick={closeGuestMenu}
                            style={flyoutLinkStyle}
                            {...flyoutHoverHandlers}
                          >
                            Home
                          </Link>
                        )}
                        {isMobile && !isAuthPage && !isCurrentNavDestination(pathname, '/login') && (
                          <Link
                            to="/login"
                            role="menuitem"
                            onClick={closeGuestMenu}
                            style={flyoutLinkStyle}
                            {...flyoutHoverHandlers}
                          >
                            Log in
                          </Link>
                        )}
                        {(isMobile && !isAuthPage && guestPrimaryLinks.length > 0) && (
                          <div style={{ borderTop: '1px solid rgba(255,255,255,0.1)', margin: '6px 0 4px' }} />
                        )}
                        {isMobile && !isAuthPage && guestPrimaryLinks.length > 0 && renderFlyoutSectionLabel('More')}
                        {!isMobile && !isAuthPage && renderFlyoutSectionLabel('Navigate')}
                        {isAuthPage && renderFlyoutSectionLabel(isMobile ? 'Account' : 'Navigate')}
                        {renderNavigateFlyoutLinks(closeGuestMenu, flyoutBlockStyle, { forGuest: true })}
                      </div>
                    </>
                  )}
                </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </nav>
  );
};

export default LandingNav;
